import {
  Form_DocumentAllowedOperation,
  useGetDocumentLazyQuery,
  useGetDocumentQuery,
} from '@/generated/graphql';
import { DownloadOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { AveliosButton } from '@components/common-layout/avelios-button';
import { FilePreview } from '@components/FilePreview';
import { Drawer, message, Modal, Spin, Steps, Upload, UploadFile, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useRouter } from 'next/router';
import { UploadRequestOption } from 'rc-upload/es/interface';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import uuid from 'react-uuid';
import { exportDocument } from '../../../helpers/document/document-export';
import { documentToUploadFile } from '../../../helpers/document/document-to-upload-file';
import { bytesToMB, proxy_uploadDocument } from '../../../helpers/document/document-upload';
import { proxy_buildDocumentDownloadUrl } from '../../../helpers/document/document-url-helper';
import { SUPPORTED_DOCUMENT_MIME_TYPES } from '../../../helpers/document/supported-document-mime-types';
import { DocumentUploadType } from '../../../helpers/document/types';
import { MimeType, mimeTypeForString } from '../../../helpers/file/mime-types';
import { useCurrentUser } from '../../../helpers/utils';
import { QuestionProps } from '../../../pages/form';
import { ScreenSizeContext } from '../../../pages/_app';

const staticProps: UploadProps = {
  accept: SUPPORTED_DOCUMENT_MIME_TYPES.join(', '),
  maxCount: 1,
  multiple: true,
  listType: 'picture',
};

const { Dragger } = Upload;

interface DocumentQuestionProps extends QuestionProps {
  clinicId: Nullable<string>;
  formRevisionGroupId: Nullable<string>;
}

export const DocumentQuestion: FC<DocumentQuestionProps> = ({
  question,
  answer,
  onAnswer,
  clinicId,
  formRevisionGroupId,
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const { isMobile } = useContext(ScreenSizeContext);

  const { currentUser, userLoading } = useCurrentUser();

  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    mimeType: MimeType;
  }>();

  const [currentStep, setCurrentStep] = useState(0);

  const [authRequired, setAuthRequired] = useState<boolean>(false);

  const staticDocumentGroupId = question?.document?.staticDocumentGroupId;
  const maxFileSize = question?.document?.maxFileSize;
  const allowedOperation = question?.document?.allowedOperation;

  const downloadOnly =
    allowedOperation === Form_DocumentAllowedOperation.DocumentAllowedOperationDownload;

  const uploadOnly =
    allowedOperation === Form_DocumentAllowedOperation.DocumentAllowedOperationUpload;

  const editMode = allowedOperation === Form_DocumentAllowedOperation.DocumentAllowedOperationEdit;

  const document = answer?.value?.document;
  const uploadedDocumentId = document?.documentId;

  const { data: documentData, loading: documentLoading } = useGetDocumentQuery({
    variables: { input: { documentId: uploadedDocumentId, clinicId } },
    skip: !uploadedDocumentId || !clinicId,
  });

  const fileName = documentData?.getDocument?.document?.document?.fileName;

  useEffect(() => {
    if (uploadOnly) {
      setCurrentStep(1);
    } else {
      setCurrentStep(!!staticDocumentGroupId && !uploadedDocumentId ? 0 : 1);
    }
  }, [staticDocumentGroupId, uploadedDocumentId, uploadOnly]);

  const [fileList, setFileList] = useState<UploadFile[]>();

  const [getDocumentQuery, { loading: getDocumentLoading }] = useGetDocumentLazyQuery();

  const getDocument = (documentId: Nullable<string>) => {
    if (!clinicId || !documentId) {
      console.warn('get document: missing props');
      return undefined;
    }

    return getDocumentQuery({
      variables: {
        input: {
          documentId: uploadedDocumentId,
          clinicId,
        },
      },
    });
  };

  useEffect(() => {
    getDocument(uploadedDocumentId)?.then((result) => {
      const uploadDocument = documentToUploadFile(result.data?.getDocument?.document);

      setFileList([uploadDocument]);
    });
  }, [clinicId]);

  const beforeUpload = (file: RcFile, maxFileSizeBytes: Nullable<number>) => {
    if (!maxFileSizeBytes) {
      return true;
    }

    if (file.size > maxFileSizeBytes) {
      const maxFileSizeMegaBytes = bytesToMB(maxFileSizeBytes);
      message.error(
        formatMessage(
          { id: 'question.document.max-file-size-exceeded' },
          { fileName: file.name, maxFileSize: maxFileSizeMegaBytes },
        ),
      );
      return Upload.LIST_IGNORE; // Prevent the file from being added to the list
    }
    return true; // Allow the file to be added
  };

  // @ts-ignore
  const props: UploadProps = {
    ...staticProps,
    beforeUpload: (file) => beforeUpload(file, maxFileSize),
    fileList,
    disabled: !currentUser,
    onChange(info) {
      let newFileList = [...info.fileList];

      newFileList = newFileList.slice(-1);

      newFileList = newFileList.map((file) => {
        if (file.response) {
          // Component will show file.url as link
          file.url = file.response.url;
        }
        return file;
      });

      setFileList(newFileList);

      const { status, name } = info.file;
      if (status === 'done') {
        message.info(formatMessage({ id: 'question.document.upload-success' }, { fileName: name }));
      }
    },
    onRemove(file) {
      if (file.status === 'done') {
        onAnswer({ document: { documentId: null } });
        message.info(
          formatMessage({ id: 'question.document.file-deleted' }, { fileName: file.name }),
        );
      }
    },
    onPreview: (file) => {
      const mimeType = mimeTypeForString(file.type);
      if (file.url && mimeType) {
        setSelectedDocument({ url: file.url, mimeType });
      } else {
        console.error(
          'Could not open preview. File url or mime type missing / invalid',
          file.url,
          mimeType,
        );
      }
    },
    showUploadList: {
      showPreviewIcon: true,
      previewIcon: <EyeOutlined />,
    },
    customRequest: (options: UploadRequestOption) => {
      const fileToUpload = options.file;

      if (clinicId && formRevisionGroupId) {
        proxy_uploadDocument(
          clinicId,
          fileToUpload,
          {
            type: DocumentUploadType.form,
            referenceId: formRevisionGroupId,
            formQuestionId: question?.id,
          },
          undefined,
          {
            onSuccess: (response) => {
              const documentId = response.data.id;

              if (documentId) {
                const uploadedFile: UploadFile = {
                  uid: documentId,
                  fileName: options.filename,
                  name: options.filename ?? uuid(),
                  status: 'done',
                  url: proxy_buildDocumentDownloadUrl(
                    clinicId,
                    documentId,
                    false,
                    false,
                  )?.toString(),
                };
                onAnswer({ document: { documentId } });
                options.onSuccess?.(uploadedFile, response.request);
              } else {
                options.onError?.(
                  {
                    name: 'error',
                    message: formatMessage(
                      { id: 'question.document.upload-error' },
                      { fileName: options.filename },
                    ),
                  },
                  options.file,
                );
              }
            },
            onVirusFound: () => {
              const errorMessage = formatMessage({
                id: 'question.document.upload-error.virus-found',
              });
              message.error(errorMessage);
              options.onError?.({ name: 'error', message: errorMessage }, options.file);
            },
            onError: (error) => {
              if (error?.response?.status === 413) {
                const errorMessage = formatMessage({ id: 'question.document.upload-error.file-too-large' });
                message.error(errorMessage);
                options.onError?.({ name: 'error', message: errorMessage }, options.file);
                return;
              }

              const errorMessage = formatMessage(
                { id: 'question.document.upload-error' },
                { fileName: options.filename },
              );
              message.error(errorMessage);
              options.onError?.({ name: 'error', message: errorMessage }, options.file);
            },
          },
        );
      }
    },
  };

  const editModeSourceUrl = useMemo(() => {
    if (!clinicId) {
      return undefined;
    }
    if (uploadedDocumentId) {
      return proxy_buildDocumentDownloadUrl(clinicId, uploadedDocumentId, false, false)?.toString();
    }
    if (staticDocumentGroupId) {
      return proxy_buildDocumentDownloadUrl(
        clinicId,
        staticDocumentGroupId,
        true,
        true,
      )?.toString();
    }
    return undefined;
  }, [clinicId, uploadedDocumentId, staticDocumentGroupId]);

  const handleEditModeUpload = (data: Uint8Array) =>
    new Promise<void>((resolve) => {
      if (!clinicId || !formRevisionGroupId) {
        resolve();
        return;
      }

      const file = new File([data], fileName ?? 'document.pdf', { type: 'application/pdf' });

      proxy_uploadDocument(
        clinicId,
        file,
        {
          type: DocumentUploadType.form,
          referenceId: formRevisionGroupId,
          formQuestionId: question?.id,
        },
        undefined,
        {
          onSuccess: (response) => {
            const documentId = response.data.id;
            if (documentId) {
              onAnswer({ document: { documentId } });
              message.success(
                formatMessage({ id: 'question.document.upload-success' }, { fileName: file.name }),
              );
            } else {
              message.error(
                formatMessage({ id: 'question.document.upload-error' }, { fileName: file.name }),
              );
            }
            resolve();
          },
          onVirusFound: () => {
            message.error(formatMessage({ id: 'question.document.upload-error.virus-found' }));
            resolve();
          },
          onError: () => {
            message.error(
              formatMessage({ id: 'question.document.upload-error' }, { fileName: file.name }),
            );
            resolve();
          },
        },
      );
    });

  const isUnauthenticated = !currentUser && !userLoading;

  const loginButton = (
    <AveliosButton
      block
      type={'primary'}
      onClick={() => {
        router.push({
          pathname: '/login',
          query: { destination: router.asPath },
        });
      }}
    >
      {formatMessage({ id: 'action.login-register' })}
    </AveliosButton>
  );

  const EditView = isUnauthenticated
    ? loginButton
    : editModeSourceUrl && (
        <FilePreview
          editable
          requiresAuthentication
          fileUrl={editModeSourceUrl}
          mimeType={MimeType.PDF}
          onUpload={handleEditModeUpload}
          uploadLabel={formatMessage({ id: 'question.document.edit.save' })}
        />
      );

  const UploadButton = (
    <div
      className={'auth-check'}
      onClick={
        !currentUser
          ? (e) => {
              setAuthRequired(true);
              e.stopPropagation();
            }
          : undefined
      }
    >
      <Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">{formatMessage({ id: 'question.document.upload.title' })}</p>
        <p className="ant-upload-hint">
          {formatMessage({ id: 'question.document.upload.description' })}
          <br />
          {formatMessage({ id: 'question.document.upload.max-file-size' })}
          <br />
          {formatMessage({ id: 'question.document.upload.allowed-formats' })}
        </p>
      </Dragger>
      {isUnauthenticated && <div style={{ marginTop: 16 }}>{loginButton}</div>}
    </div>
  );

  const loading = documentLoading || userLoading || getDocumentLoading;

  if (editMode) {
    return <Spin spinning={loading}>{EditView}</Spin>;
  }

  return (
    <Spin spinning={loading}>
      {!!staticDocumentGroupId && !downloadOnly && !uploadOnly && (
        <Steps
          style={{ marginBottom: 32 }}
          current={currentStep}
          onChange={(value) => {
            setCurrentStep(value);
          }}
          items={[
            {
              title: formatMessage({ id: 'question.document.upload.download' }),
            },
            {
              title: formatMessage({ id: 'question.document.upload.upload' }),
            },
          ]}
        />
      )}
      {currentStep === 0 ? (
        <AveliosButton
          block
          size={'large'}
          disabled={!clinicId || !staticDocumentGroupId}
          icon={<DownloadOutlined />}
          onClick={() => {
            if (clinicId && staticDocumentGroupId) {
              const documentUrl = proxy_buildDocumentDownloadUrl(
                clinicId,
                staticDocumentGroupId,
                true,
                true,
              );
              if (documentUrl) {
                exportDocument(documentUrl)?.then(() => {
                  onAnswer({ document: { documentId: document?.documentId, isDownloaded: true } });
                });
                if (!downloadOnly) {
                  setCurrentStep(1);
                }
              }
            }
          }}
          style={{ overflow: 'hidden' }}
        >
          <span>
            {formatMessage(
              { id: 'question.document.download-document' },
              // TODO: replace with filename
              { fileName: fileName },
            )}
          </span>
        </AveliosButton>
      ) : (
        UploadButton
      )}
      {selectedDocument && (
        <Drawer
          bodyStyle={{ padding: 0 }}
          width={isMobile ? '100%' : '50%'}
          open={!!selectedDocument}
          onClose={() => {
            setSelectedDocument(undefined);
          }}
        >
          <FilePreview
            requiresAuthentication
            fileUrl={selectedDocument.url}
            mimeType={selectedDocument.mimeType}
          />
        </Drawer>
      )}
      <Modal
        title={formatMessage({ id: 'question.document.login-required' })}
        onCancel={() => setAuthRequired(false)}
        footer={false}
        open={authRequired}
      >
        <p style={{ color: '#3e3e3e' }}>
          {formatMessage({ id: 'question.document.login-required.description' })}
        </p>
        {loginButton}
      </Modal>
    </Spin>
  );
};
